import { createTheme } from "@mui/material";

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: "#005CA6", // Blue
      light: "#148DD3", // Light Blue
      dark: "#003A6E", // Dark Blue
      contrastText: "#FFF", // White
    },
    secondary: {
      main: "#09B0F2",
      contrastText: "#fff",
    },
    neutral: {
      light: "#DBDBDD", // Light Gray
      dark: "#343434", // Dark Gray
      main: "#64748B", // Medium Gray
      contrastText: "#fff",
    },

    success: {
      main: "#009F5C",
    },
    error: {
      main: "#C52F00",
    },
    mode: "light",
  },

  typography: {
    fontFamily: "Inter",
  },

  components: {
    MuiAlert: {
      variants: [
        {
          props: { severity: "error" },
          style: {
            border: "3px solid #FF0000",
            borderRadius: 20,
            height: 62,
            display: "flex",
            alignItems: "center",
            fontSize: 14,
            fontWeight: 500,
          },
        },
      ],
      styleOverrides: {
        root: {
          padding: "0px 10px",
          fontSize: 12,
        },
      },
    },

    MuiChip: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          height: 18,
          fontSize: 12,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          fontSize: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingRight: "0px!important",
        },
        noOptions: {
          fontSize: 12,
        },
        option: {
          fontSize: 12,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        autoComplete: "off", // "new-password",
      },
      styleOverrides: {
        root: {
          "&.XtremeSmall": {
            fontWeight: 700,
            fontSize: 14,
            color: "#231F20",
            height: 22,
            padding: 0,
            margin: 0,
          },
        },
      },
    },

    MuiRadio: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          padding: 0,
          margin: 0,
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: 12,
          color: "#4B4B4B",
          fontWeight: 400,
          paddingLeft: 2,
        },
        root: {
          margin: 0,
          padding: 0,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 12,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        autoComplete: "off", // "new-password",
      },
      variants: [
        {
          props: { size: "small", multiline: false },
          style: {
            height: 32,
            fontSize: 14,
          },
        },
        {
          props: { size: "small", multiline: true },
          style: {
            minHeight: 32,
            fontSize: 14,
            padding: 0,
            margin: 0,
          },
        },
        {
          props: { size: "medium" },
          style: {
            height: 40,
            fontSize: 14,
          },
        },
      ],
      styleOverrides: {
        root: {
          padding: 0,
          margin: 0,
          backgroundColor: "white",
          borderRadius: 5,
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        autoComplete: "new-password",
      },
      variants: [
        {
          props: { size: "small", multiline: false },
          style: {
            height: 32,
            fontSize: 14,
          },
        },
        {
          props: { size: "small", multiline: true },
          style: {
            minHeight: 32,
            fontSize: 14,
          },
        },
        {
          props: { size: "medium" },
          style: {
            height: 40,
            fontSize: 14,
          },
        },
      ],
      styleOverrides: {
        input: {
          padding: 6,
          margin: 0,
        },
      },
    },

    MuiInputBase: {
      defaultProps: {
        autoComplete: "new-password",
      },
      variants: [
        {
          props: { size: "small", multiline: false },
          style: {
            height: 32,
            fontSize: 14,
          },
        },
        {
          props: { size: "small", multiline: true },
          style: {
            minHeight: 32,
            fontSize: 14,
          },
        },
        {
          props: { size: "medium" },
          style: {
            height: 40,
            fontSize: 8,
          },
        },
      ],
      styleOverrides: {
        root: {
          backgroundColor: "white",
          borderRadius: 5,
        },
      },
    },

    MuiTable: {
      defaultProps: {
        size: "small",
        "aria-label": "a dense table",
      },
    },
    MuiTableCell: {
      defaultProps: {
        padding: "none",
        align: "left",
      },
      styleOverrides: {
        root: {
          color: "inherit",
          fontWeight: "inherit",
          paddingRight: 4,
        },
      },
    },

    MuiButton: {
      defaultProps: {
        size: "small",
      },
      variants: [
        {
          props: { color: "primary", variant: "outlined" },
          style: {
            "&:hover": {
              backgroundColor: "white",
            },
          },
        },
        {
          props: { color: "info" },
          style: {
            "&:hover": {
              backgroundColor: "#ffffff",
              color: "black",
              borderColor: "black",
            },
            backgroundColor: "white",
            border: "1px solid #00000099",
            color: "#00000099",
            fontsize: 16,
          },
        },
        {
          props: { color: "success" },
          style: {
            "&.MuiButton-root.MuiButton-contained.Mui-disabled": {
              backgroundColor: "#009F5C",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          minWidth: 40,
          textTransform: "capitalize",
          borderRadius: 4,
          fontSize: 14,
          fontWeight: 500,
          "&.MuiButton-root.MuiButton-contained.Mui-disabled": {
            opacity: 0.4,
            backgroundColor: "#005CA6",
            color: "white",
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      variants: [
        {
          props: { size: "small", orientation: "horizontal" },
          style: {
            height: 30,
            borderRadius: 4,
          },
        },
        {
          props: { size: "medium", orientation: "horizontal" },
          style: {
            height: 40,
            borderRadius: 4,
          },
        },
        {
          props: { size: "large", orientation: "horizontal" },
          style: {
            height: 40,
            borderRadius: 4,
          },
        },
      ],
      styleOverrides: {
        root: {
          backgroundColor: "#F6F6F6",
          textTransform: "none",
          border: "1px solid #B3B3B3",
        },
      },
    },
    MuiToggleButton: {
      variants: [
        {
          props: { size: "small" },
          style: {
            height: 30,
            borderRadius: 3,
            fontSize: 12,
            lineHeight: "14px",
          },
        },
        {
          props: { size: "medium" },
          style: {
            height: 40,
            borderRadius: 4,
            fontSize: 16,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 20,
            paddingRight: 20,
          },
        },
        {
          props: { size: "large" },
          style: {
            height: 40,
            borderRadius: 4,
            fontSize: 16,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 20,
            paddingRight: 20,
          },
        },
      ],

      styleOverrides: {
        root: {
          textTransform: "none",
          color: "#231F20",
          borderWidth: 0,
          boxShadow: "2px 0px 4px 0px #0000002E inset",
          fontWeight: 400,

          "&.MuiToggleButton-root.Mui-selected": {
            borderRadius: 3,
            color: "white",
            backgroundColor: "#09B0F2",
            boxShadow: "-5px 0px 12px 0px #00000017",
          },
          "&.MuiToggleButton-root.Mui-disabled": {
            backgroundColor: "#F6F6F6",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#148DD3",
          color: "white",
          fontSize: 12,
          padding: 10,
        },
      },
    },
  },
});

export { defaultTheme };
